import axios from 'axios';
import ApiService from './ApiService';

/**
 * YouGuide One admin API client.
 *
 * Its own file, for the same reason TelnaApiService has one: ApiService.js is a
 * shared surface every page imports, and burying a dozen more static methods in
 * it makes unrelated merge conflicts likely. YouGuide One is its own product
 * with its own routes.
 *
 * The base URL comes from ApiService rather than being re-declared — that file
 * carries the dev/prod switch, and a second copy goes stale the first time
 * someone flips it.
 */

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + localStorage.getItem('token'),
});

const base = () => `${ApiService.baseURL}/one/v1/admin`;

/**
 * Unwrap an axios error into something a page can act on.
 *
 * `code` is the part that matters. The panel has to tell "that order is a guest
 * order" apart from "already refunded" apart from "the server is down", and
 * those are three different 4xx/5xx with different codes — not three flavours
 * of "failed". A page that only has a message string cannot offer the right
 * next step.
 */
const toError = (error, fallback) => {
    const d = error?.response?.data;
    const err = new Error(d?.message || d?.error || error.message || fallback);
    err.code = d?.error || 'REQUEST_FAILED';
    err.status = error?.response?.status;
    return err;
};

const OneApiService = {
    /* ------------------------------------------------------------ orders */

    async listOrders(params = {}) {
        try {
            const res = await axios.get(`${base()}/orders`, {
                headers: authHeaders(),
                params,
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not load orders');
        }
    },

    /**
     * The eSIM LINES behind orders — what the Telna page needs.
     *
     * `TelnaOrder` holds only orders raised in that panel, so the Orders tab
     * was empty for every real sale. This is per-SIM, which is the question
     * actually being asked: which ICCID, how much was sold, how much is left.
     */
    async listEsimLines(params = {}) {
        try {
            const res = await axios.get(`${base()}/esim-lines`, {
                headers: authHeaders(),
                params,
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not load eSIM lines');
        }
    },

    /** Sold, used, what Telna will bill, and what that leaves. */
    async esimStats(provider = 'telna') {
        try {
            const res = await axios.get(`${base()}/esim-stats`, {
                headers: authHeaders(),
                params: { provider },
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not total the eSIMs');
        }
    },

    /** Ends service at the provider and marks the line cancelled. */
    async stopEsimLine(id) {
        try {
            const res = await axios.post(`${base()}/esim-lines/${id}/stop`, {}, {
                headers: authHeaders(),
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not stop that eSIM');
        }
    },

    /** Removes it from the customer's app. Does NOT stop the SIM. */
    async hideEsimLine(id, unhide = false) {
        try {
            const res = await axios.post(`${base()}/esim-lines/${id}/hide`, { unhide }, {
                headers: authHeaders(),
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not update that eSIM');
        }
    },

    async getOrder(orderNumber) {
        try {
            const res = await axios.get(`${base()}/orders/${orderNumber}`, {
                headers: authHeaders(),
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not load that order');
        }
    },

    /**
     * Refund to wallet credit.
     *
     * `amount` omitted means the whole order. The backend refuses to return
     * more than the order ever took, so a double-clicked button cannot pay
     * twice — but the page should still disable it while in flight, because a
     * second request that fails looks to the operator like the first one did.
     */
    async refundOrder(orderNumber, { amount, note } = {}) {
        try {
            const res = await axios.post(
                `${base()}/orders/${orderNumber}/refund`,
                { amount, note },
                { headers: authHeaders() },
            );
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not refund that order');
        }
    },

    /* ---------------------------------------------------------- settings */

    async getSettings() {
        try {
            const res = await axios.get(`${base()}/settings`, { headers: authHeaders() });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not load settings');
        }
    },

    /**
     * Save only the keys passed. The backend writes exactly what it is sent, so
     * a partial save cannot blank the rest — and it returns the values the app
     * will actually be served, after coercion, rather than echoing the input.
     */
    async updateSettings(patch) {
        try {
            const res = await axios.put(`${base()}/settings`, patch, {
                headers: authHeaders(),
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not save settings');
        }
    },

    /* ----------------------------------------------------- partner apps */

    async listConfigurations() {
        try {
            const res = await axios.get(`${base()}/configurations`, {
                headers: authHeaders(),
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not load partner apps');
        }
    },

    async createConfiguration(body) {
        try {
            const res = await axios.post(`${base()}/configurations`, body, {
                headers: authHeaders(),
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not create that partner app');
        }
    },

    async updateConfiguration(configId, body) {
        try {
            const res = await axios.put(`${base()}/configurations/${configId}`, body, {
                headers: authHeaders(),
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not save that partner app');
        }
    },

    /**
     * Upload a partner logo and get back the key to save plus a URL to show.
     *
     * SEPARATE FROM SAVING THE CONFIGURATION on purpose: the operator uploads,
     * sees the result in the preview, and only then saves. Folding it into the
     * save would mean a rejected image lost the rest of the form with it.
     *
     * No Content-Type header — the browser sets the multipart boundary, and
     * overriding it produces a body the server cannot parse.
     */
    async uploadLogo(file) {
        try {
            const body = new FormData();
            body.append('logo', file);
            const res = await axios.post(`${base()}/configurations/logo`, body, {
                headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            });
            return res.data;
        } catch (e) {
            throw toError(e, 'Could not upload that image');
        }
    },

    /* ------------------------------------------------------------ public */

    /**
     * The customer-facing destinations list, read with the admin token.
     *
     * Used by the settings page to show what the app will actually serve after
     * a change — the same endpoint the phone calls, so the preview cannot drift
     * from reality the way a re-implementation would.
     */
    async previewDestinations() {
        try {
            const res = await axios.get(`${ApiService.baseURL}/one/v1/destinations`, {
                headers: authHeaders(),
            });
            // The endpoint answers an ENVELOPE — `{ mode, destinations }` —
            // because the provider switch decides which of two products is
            // being sold. Returning `res.data` straight through handed the
            // page an object where it expected an array and crashed the tab.
            const d = res.data;
            return {
                mode: d?.mode ?? null,
                destinations: Array.isArray(d?.destinations)
                    ? d.destinations
                    : (Array.isArray(d) ? d : []),
            };
        } catch (e) {
            throw toError(e, 'Could not preview destinations');
        }
    },
};

export default OneApiService;
